/**
 * /reports/payroll-cost — payroll cost by month and department from LOCKED
 * payroll (persona round 2026-10). Data: GET /api/v1/hr-reports/payroll-cost.
 */

import { Fragment, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { IndianRupee } from "lucide-react";
import { hrReportsApi, toApiError } from "@/lib/api";
import type { PayrollCostLine } from "@/lib/api/hr-reports";
import { ReportsShell } from "@/components/reports/reports-shell";
import { ReportDownloadButton } from "@/components/reports/report-download-button";
import { EmptyState } from "@/components/shared/empty-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPaise } from "@/lib/expense-format";
import { STICKY_FIRST_COL } from "@/lib/pay-table";
import { cn } from "@/lib/utils";
import { todayIndiaISO } from "@/lib/payroll-month";
import { fyStartMonth } from "@/lib/reports";
import { useBelowSm } from "@/lib/use-below-sm";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/reports/payroll-cost")({
  component: PayrollCostReportPage,
});

/** The month row's muted shade, solid so figures scroll under the pinned
 *  label (bg-muted/40 is see-through). */
const MONTH_ROW_BG = "bg-[color-mix(in_oklab,var(--color-muted)_40%,var(--color-card))]";

function Cells({ l }: Readonly<{ l: PayrollCostLine }>) {
  return (
    <>
      <TableCell className="text-right tabular-nums">{l.employees}</TableCell>
      <TableCell className="text-right font-mono tabular-nums">{formatPaise(l.gross_paise)}</TableCell>
      <TableCell className="text-right font-mono tabular-nums">
        {formatPaise(l.employee_deductions_paise)}
      </TableCell>
      <TableCell className="text-right font-mono tabular-nums">{formatPaise(l.net_paise)}</TableCell>
      <TableCell className="text-right font-mono tabular-nums">{formatPaise(l.employer_pf_paise)}</TableCell>
      <TableCell className="text-right font-mono tabular-nums">{formatPaise(l.employer_esi_paise)}</TableCell>
      <TableCell className="text-right font-mono font-semibold tabular-nums">
        {formatPaise(l.cost_to_company_paise)}
      </TableCell>
    </>
  );
}

/** Phones: one card per month with the three figures people come for —
 *  gross, net and total cost to company (the full table is 8 columns,
 *  with CTC 6 columns off-screen). */
function MonthCard({
  label,
  l,
  emphasis = false,
}: Readonly<{ label: string; l: PayrollCostLine; emphasis?: boolean }>) {
  return (
    <li className={cn("rounded-lg border bg-card p-4", emphasis && "border-2")}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-semibold">{label}</h3>
        <span className="text-xs text-muted-foreground">
          {l.employees} paid
        </span>
      </div>
      <dl className="mt-2 space-y-1 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">Gross Pay</dt>
          <dd className="font-mono tabular-nums">{formatPaise(l.gross_paise)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">Net Pay</dt>
          <dd className="font-mono tabular-nums">{formatPaise(l.net_paise)}</dd>
        </div>
        <div className="flex justify-between gap-3 font-semibold">
          <dt>Total Cost to Company</dt>
          <dd className="font-mono tabular-nums">{formatPaise(l.cost_to_company_paise)}</dd>
        </div>
      </dl>
    </li>
  );
}

function PayrollCostReportPage() {
  const phone = useBelowSm();
  const [allColumns, setAllColumns] = useState(false);
  const today = todayIndiaISO();
  const [from, setFrom] = useState(fyStartMonth(today));
  const [to, setTo] = useState(today.slice(0, 7));
  const valid = /^\d{4}-\d{2}$/.test(from) && /^\d{4}-\d{2}$/.test(to) && to >= from;
  const q = useQuery({
    queryKey: ["hr-reports", "payroll-cost", from, to],
    queryFn: ({ signal }) => hrReportsApi.getPayrollCost({ from, to }, signal),
    enabled: valid,
  });
  const rep = q.data;

  return (
    <ReportsShell
      title="Payroll Cost"
      description="What payroll cost the company each month, by department. Only locked payroll is counted."
      actions={
        <ReportDownloadButton
          report="payroll-cost"
          params={{ from, to }}
          filename={`payroll-cost-${from}-to-${to}.csv`}
          disabled={!valid || !rep || rep.months.length === 0}
        />
      }
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="pc-from">From Month</Label>
            <Input id="pc-from" type="month" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="pc-to">To Month</Label>
            <Input id="pc-to" type="month" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </div>
          {!valid && (
            <p className="text-sm text-destructive">The end month must be on or after the start month.</p>
          )}
        </div>

        {q.isLoading && <Skeleton className="h-64" />}
        {q.isError && (
          <Alert variant="destructive">
            <AlertTitle>Couldn&apos;t load the report</AlertTitle>
            <AlertDescription>{toApiError(q.error).message}</AlertDescription>
          </Alert>
        )}
        {rep && rep.months.length === 0 && (
          <EmptyState
            icon={IndianRupee}
            title="No Locked Payroll in This Period"
            description="Payroll cost appears here once a month's payroll is locked."
          />
        )}
        {rep && rep.months.length > 0 && phone && (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            aria-pressed={allColumns}
            onClick={() => setAllColumns((v) => !v)}
          >
            {allColumns ? "Show Monthly Summary" : "Show All Columns"}
          </Button>
        )}
        {rep && rep.months.length > 0 && phone && !allColumns && (
          <ul className="space-y-3" data-testid="payroll-cost-cards">
            {rep.months.map((m) => (
              <MonthCard key={m.label} label={m.label} l={m.total} />
            ))}
            <MonthCard label="All Months" l={rep.total} emphasis />
          </ul>
        )}
        {rep && rep.months.length > 0 && (!phone || allColumns) && (
          <Card>
            <CardContent className="overflow-x-auto px-0 pt-4 sm:px-6">
              <Table data-testid="payroll-cost-table">
                <TableHeader>
                  {/* Phones: headers stay on one line and the row label is
                      pinned while the figures scroll (docs/71 §10). */}
                  <TableRow className="max-sm:[&>th:not(:first-child)]:whitespace-nowrap">
                    <TableHead className={cn(STICKY_FIRST_COL, "min-w-[9rem]")}>Month / Department</TableHead>
                    <TableHead className="text-right">Employees Paid</TableHead>
                    <TableHead className="text-right">Gross Pay</TableHead>
                    <TableHead className="text-right">Employee Deductions</TableHead>
                    <TableHead className="text-right">Net Pay</TableHead>
                    <TableHead className="text-right">Employer PF</TableHead>
                    <TableHead className="text-right">Employer ESI</TableHead>
                    <TableHead className="text-right">Total Cost to Company</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rep.months.map((m) => (
                    <Fragment key={m.label}>
                      <TableRow className="bg-muted/40 font-medium">
                        <TableCell className={cn(STICKY_FIRST_COL, MONTH_ROW_BG)}>{m.label}</TableCell>
                        <Cells l={m.total} />
                      </TableRow>
                      {m.by_department.map((d) => (
                        <TableRow key={`${m.label}-${d.department}`}>
                          <TableCell className={cn(STICKY_FIRST_COL, "pl-8 text-muted-foreground")}>{d.department}</TableCell>
                          <Cells l={d} />
                        </TableRow>
                      ))}
                    </Fragment>
                  ))}
                  <TableRow className="border-t-2 font-semibold">
                    <TableCell className={STICKY_FIRST_COL}>All months</TableCell>
                    <Cells l={rep.total} />
                  </TableRow>
                </TableBody>
              </Table>
              <p className="mt-3 px-6 text-xs text-muted-foreground sm:px-0">
                Total cost to company = gross pay + employer PF + employer ESI. EDLI and PF admin
                charges are not included. Departments are each person&apos;s current department.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </ReportsShell>
  );
}
